import QRCodeStyling from "qr-code-styling";
import { censorPhoneNumber } from "./helpers";
import moment from "moment";

export const getTicketCanvas = () => {
    let canvas, ctx;
    const width = 640;
    const height = 220;
    canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    ctx = canvas.getContext("2d");
    if (ctx) {
        ctx.fillStyle = "#fff";
        ctx.fillRect(0, 0, width, height);
    }
    return { canvas, ctx };
};

export const wrapText = (context, text, x, y, maxWidth) => {
    const words = text.split(" ");
    let line = "";
    let lineY = y;
    let lineCount = 0;

    for (let n = 0; n < words.length && lineCount < 2; n++) {
        const testLine = line + words[n] + " ";
        const metrics = context.measureText(testLine);
        const testWidth = metrics.width;

        if (testWidth > maxWidth && n > 0) {
            context.fillText(line, x, lineY);
            line = words[n] + " ";
            lineY += 48;
            lineCount++;
        } else {
            line = testLine;
        }
    }
    context.fillText(line, x, lineY);
};

// Fungsi untuk membatasi panjang nama
export const limitNameLength = (name) => {
    if (!name) return "";
    if (name.length <= 24) return name;

    const words = name.split(" ");
    let result = "";
    let currentLength = 0;

    for (const word of words) {
        if (currentLength + word.length + 1 <= 24) {
            result += (result ? " " : "") + word;
            currentLength += word.length + (result ? 1 : 0);
        } else {
            break;
        }
    }

    return result;
};

// Fungsi untuk membatasi nama menjadi 2 baris
export const limitToTwoLines = (ctx, text, maxWidth) => {
    const words = text.split(" ");
    const lines = [];
    let currentLine = words[0];

    for (let i = 1; i < words.length; i++) {
        const word = words[i];
        const width = ctx.measureText(currentLine + " " + word).width;
        if (width < maxWidth) {
            currentLine += " " + word;
        } else {
            lines.push(currentLine);
            currentLine = word;
        }
    }
    lines.push(currentLine);
    return lines.slice(0, 2); // Hanya ambil 2 baris pertama
};

export const drawParentName = (ctx, name, x, y, maxWidth = 250) => {
    if (!name) return y;

    const lines = limitToTwoLines(ctx, name, maxWidth);
    lines.forEach((line, index) => {
        ctx.fillText(line, x, y + index * 22);
    });

    return y + lines.length * 22 + 5;
};

export const drawChildTicket = async (ctx, data) => {
    ctx.fillStyle = "black";
    const qrcode = new QRCodeStyling({
        data: data.code,
        width: 120,
        height: 120,
        colorDark: "#000",
        type: "canvas",
        margin: 0,
        backgroundOptions: {
            color: "#fff",
        },
        qrOptions: {
            errorCorrectionLevel: "M",
        },
    });
    await qrcode._canvasDrawingPromise;
    if (qrcode._domCanvas) {
        ctx.drawImage(qrcode._domCanvas, 0, 0);
    }
    let name = data.participant_name;
    ctx.font = "36px Arial";
    ctx.textAlign = "left";
    // Render text dengan wrap
    wrapText(ctx, name, 130, 30, 300); // 300 adalah lebar maksimum dalam pixel

    if (data.group_id) {
        ctx.textBaseline = "top";
        ctx.font = "36px Arial";
        ctx.textAlign = "center";
        let groupId = data.group_id.toString().padStart(3, "0");
        ctx.fillText(`G.${groupId}`, 60, 130);
    }
};

export const drawParentTicket = async (ctx, data) => {
    ctx.fillStyle = "black";
    let code = data.code;
    if (data.code) {
        code = "https://q.gms.church/ek/" + data.code;
    }
    const qrcode = new QRCodeStyling({
        data: code,
        width: 120,
        height: 120,
        colorDark: "#000",
        type: "canvas",
        margin: 0,
        backgroundOptions: {
            color: "#fff",
        },
        qrOptions: {
            errorCorrectionLevel: "M",
        },
    });
    const canvasWidth = ctx.canvas.width;
    const x = (canvasWidth - 120) / 2 - 30; // 120 adalah lebar QR code
    await qrcode._canvasDrawingPromise;
    if (qrcode._domCanvas) {
        // Hitung posisi tengah canvas
        const y = 0; // 120 adalah tinggi QR code
        ctx.drawImage(qrcode._domCanvas, x, y);
    }
    let name = data.participant_name;
    ctx.font = "24px Arial";
    ctx.textAlign = "center";

    const textX = canvasWidth / 2 - 30; // Posisi tengah canvas secara horizontal
    wrapText(ctx, name, textX, 160, 400); // Posisi x di tengah canvas, y tetap 160
};

export const drawNameTicket = async (ctx, data) => {
    ctx.fillStyle = "black";
    const canvasWidth = ctx.canvas.width;
    const canvasHeight = ctx.canvas.height;
    let name = "";
    if (data.participant) {
        name = data.participant.name;
    } else if (data.participant_name) {
        name = data.participant_name;
    } else if (data.name) {
        name = data.name;
    }
    ctx.font = "40px Arial";
    ctx.textAlign = "center";

    // Hitung jumlah baris yang akan dihasilkan
    const lines = limitToTwoLines(ctx, name, 380);
    const lineHeight = 48; // Tinggi per baris sesuai dengan wrapText
    const totalTextHeight = lines.length * lineHeight;

    // Hitung posisi Y agar teks tetap di tengah
    const textX = canvasWidth / 2 - 24; // Posisi tengah canvas secara horizontal
    const textY = (canvasHeight - totalTextHeight) / 2 + lineHeight / 2; // Posisi Y yang disesuaikan dengan offset

    wrapText(ctx, name, textX, textY, 380); // Posisi x dan y di tengah canvas
};

export const drawEnvelopeTicket = async (ctx, data) => {
    const width = ctx.canvas.width;
    let createdAt = data.created_at;
    if (!createdAt) {
        createdAt = new Date().toISOString();
    }
    const datetime = moment(createdAt).locale("id").format("DD MMM YY, HH:mm");
    // Set text alignment to center
    ctx.textAlign = "left";
    ctx.textBaseline = "top";
    let name = "";
    if (data.participant) {
        name = data.participant.name;
    } else if (data.participant_name) {
        name = data.participant_name;
    } else if (data.name) {
        name = data.name;
    }
    let phoneNumber = "";
    if (data.participant) {
        phoneNumber = data.participant.phone;
    } else if (data.phone) {
        phoneNumber = data.phone;
    } else if (data.phone_number) {
        phoneNumber = data.phone_number;
    }

    const centerX = width / 2 - 20; // Posisi X di tengah canvas
    let centerY = 15; // Posisi Y di tengah canvas
    // draw logo
    let logoImage = new Image();
    let promise = new Promise((resolve, reject) => {
        logoImage.src = `/img/dv.png`;
        logoImage.onload = () => {
            const logoWidth = 200; // Sesuaikan ukuran lebar yang diinginkan
            const aspectRatio = logoImage.width / logoImage.height;
            const logoHeight = logoWidth / aspectRatio;
            // ctx?.drawImage(logoImage, centerX - 200, 0, logoWidth, logoHeight);
            ctx?.drawImage(
                logoImage,
                centerX - 200 - 60,
                centerY - 15,
                logoWidth,
                logoHeight
            );
            resolve(true);
        };
        logoImage.onerror = () => {
            reject("failed to load image");
        };
    });
    await promise;
    ctx.fillStyle = "black";
    // tulis text
    ctx.font = `39px BarlowMedium`;
    const arrNames = name.toUpperCase().split(" ");
    if (arrNames.length > 1) {
        ctx.fillText(arrNames[0], centerX, centerY);
        centerY += 33;
        ctx.fillText(arrNames[1], centerX, centerY);
    } else {
        ctx.fillText(arrNames[0], centerX, centerY);
    }
    ctx.font = `28px Barlow`;
    centerY += 45;
    if (phoneNumber) {
        ctx.fillText(censorPhoneNumber(phoneNumber), centerX, centerY);
    }
    centerY += 35;
    ctx.fillText(datetime, centerX, centerY);
};
